import { API_BASE } from "../config/constants";
import { normalizeStaffRoleKey, usesWaiterBaselinePermissions } from "./staffAccess";
import { isFullOfflineModeEnabled } from "./fullOfflineMode";
import { getOfflineRolePermissions } from "../offline/staffStore";

export const ROLE_UI_PERMISSIONS_EVENT = "role-ui-permissions-updated";
export const ROLE_UI_PERMISSIONS_STORAGE_KEY = "roleUiPermissionsV1";
export const CONFIGURABLE_ROLES = ["cashier", "chef", "waiter", "attendant"];
export const REPORT_DATE_ACCESS = { TODAY_ONLY: "today_only", FULL: "full" };

export const REPORT_KEYS = [
  "restaurantsOverview", "customExcelReport", "dayWiseItemReport", "dayWiseCategoryReport",
  "dayWiseKotReport", "monthWiseItemReport", "paymentMethodReport", "transactionSummaryReport", "splitPaymentReport",
  "creditPaymentReport", "financeReport", "profitReport", "salesList", "partyBills",
  "salesReport", "topProducts", "topWaiters", "hourlySales", "categoryPerformance",
  "tableUtilization", "tableWiseProductSales", "monthlySummary", "stockSummaryReport",
  "dailyUsageReport", "lowStockReport", "itemPerformanceReport", "taxReport",
  "discountedOrdersReport", "tableWiseSalesList", "dayBookEntryReport",
  "orderEditHistory", "deletedOrders",
];

export const MENU_KEYS = {
  drawer: ["dashboard", "notifications", "allShortcuts", "printerConnect", "scanMenuWithAI", "qrTableOrder", "qrCodeManagement", "kotManagement", "salesList", "category", "addProduct", "productList", "digitalMenu", "bulkTax", "priceLists", "rolePermissions", "inventoryStockIn", "inventoryUsed", "inventoryManage", "productReorder", "tableManagement", "staffList", "partyManagement", "expenseAndIncome", "khatabook", "profile", "regionalTax", "printerSettings", "multiplePrinters", "customizeDrawer", "calculator", "language", "findUsOn", "keyboardShortcuts", "helpCenter", "loginHelp", "supportChat", "feedbackHub", "policies", "planInfo", "smartInsights", "newOrder", "quickBill", "myKOTs"],
  tabs: ["Dashboard", "Product List", "QuickBill", "TableScreen", "Reports", "More", "Profile", "WaiterProfile"],
  more: ["TableScreen", "SalesListScreen", "BillingHistory", "DeletedOrders", "PartyBillsScreen", "AddCategory", "AddProduct", "ProductList", "AddParty", "AddTable", "MenuScanner", "MenuScannerV2", "QRCodeManagementScreen", "QrTableOrder", "FinanceTracker", "FinanceReport", "ProfitReport", "DayBookEntryReport", "Khatabook", "Calculator", "RolePermissions", "Profile", "ConnectBlueTooth", "POSSettings", "HelpCenterScreen", "SupportChatScreen", "FeedbackHub", "SalesReports", "TransactionSummaryReport", "DayWiseItemReport", "DayWiseCategoryReport", "DayWiseKOTReport", "MonthWiseItemReport", "MonthlySummary", "PaymentMethodReport", "SplitPaymentReport", "CreditPaymentReport", "TopProductsReport", "TopWaitersReport", "HourlySalesReport", "CategoryPerformanceReport", "TableUtilizationReport", "TableWiseProductSalesReport", "StockSummaryReport", "DailyUsageReport", "LowStockReport", "ItemPerformanceReport"],
};

const normalizeBoolean = (value, fallback) => {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || String(value).toLowerCase() === "true") return true;
  if (value === 0 || value === "0" || String(value).toLowerCase() === "false") return false;
  return fallback;
};
const normalizeFlags = (raw, fallback) => Object.keys(fallback).reduce((next, key) => ({
  ...next,
  [key]: normalizeBoolean(raw?.[key], fallback[key]),
}), {});

export const defaultRoleUiPermissions = (role = "") => {
  const normalized = normalizeStaffRoleKey(role);
  const reports = { page: true, dateFilter: REPORT_DATE_ACCESS.FULL, items: Object.fromEntries(REPORT_KEYS.map((key) => [key, true])) };
  const menus = Object.fromEntries(Object.entries(MENU_KEYS).map(([section, keys]) => [section, Object.fromEntries(keys.map((key) => [key, true]))]));
  menus.customerManagement = usesWaiterBaselinePermissions(normalized)
    ? { page: false, create: false, edit: false, delete: false }
    : { page: true, create: true, edit: true, delete: true };
  if (normalized === "chef") {
    reports.page = false;
    Object.keys(reports.items).forEach((key) => { reports.items[key] = false; });
    ["drawer", "tabs", "more"].forEach((section) => Object.keys(menus[section]).forEach((key) => { menus[section][key] = false; }));
    menus.drawer.kotManagement = true;
    menus.customerManagement = { page: false, create: false, edit: false, delete: false };
  }
  if (usesWaiterBaselinePermissions(normalized)) {
    reports.page = false;
    Object.keys(reports.items).forEach((key) => { reports.items[key] = false; });
    ["drawer", "tabs", "more"].forEach((section) => Object.keys(menus[section]).forEach((key) => { menus[section][key] = false; }));
    ["newOrder", "quickBill", "myKOTs", "profile"].forEach((key) => { menus.drawer[key] = true; });
    ["TableScreen", "QuickBill", "WaiterProfile"].forEach((key) => { menus.tabs[key] = true; });
  }
  return { reports, menus };
};

export const normalizeRoleUiPermissions = (raw, role = "") => {
  const base = defaultRoleUiPermissions(role);
  return {
    reports: {
      page: normalizeBoolean(raw?.reports?.page, base.reports.page),
      dateFilter: raw?.reports?.dateFilter === REPORT_DATE_ACCESS.TODAY_ONLY ? REPORT_DATE_ACCESS.TODAY_ONLY : REPORT_DATE_ACCESS.FULL,
      items: normalizeFlags(raw?.reports?.items, base.reports.items),
    },
    menus: {
      drawer: normalizeFlags(raw?.menus?.drawer, base.menus.drawer),
      tabs: normalizeFlags(raw?.menus?.tabs, base.menus.tabs),
      more: normalizeFlags(raw?.menus?.more, base.menus.more),
      customerManagement: normalizeFlags(raw?.menus?.customerManagement, base.menus.customerManagement),
    },
  };
};

const readCache = () => {
  try { return JSON.parse(localStorage.getItem(ROLE_UI_PERMISSIONS_STORAGE_KEY) || "{}"); } catch { return {}; }
};
const cacheKeyFor = (restaurantId, role) =>
  `${isFullOfflineModeEnabled() ? "offline:" : ""}${restaurantId}:${normalizeStaffRoleKey(role)}`;
export const getCachedRoleUiPermissions = (user) => {
  const role = normalizeStaffRoleKey(user?.role || user?.staffrole || user?.staff_role || user?.user_role);
  const restaurantId = user?.restaurantid || user?.restaurant_id || user?.owner_id || user?.ownerid || user?.parent_id || user?.id;
  const cached = readCache();
  return cached?.[cacheKeyFor(restaurantId, role)] || null;
};
export const getReportDateAccess = (user) => {
  const role = normalizeStaffRoleKey(user?.role || user?.staffrole || user?.staff_role || user?.user_role);
  if (!CONFIGURABLE_ROLES.includes(role)) return REPORT_DATE_ACCESS.FULL;
  const permissions = getCachedRoleUiPermissions(user);
  // Until permissions load, staff must not request historical reports.
  if (!permissions) return REPORT_DATE_ACCESS.TODAY_ONLY;
  return normalizeRoleUiPermissions(permissions, role).reports.dateFilter;
};
export const cacheRoleUiPermissions = ({ restaurantId, role, permissions }) => {
  if (!restaurantId || !role || typeof window === "undefined") return;
  const cache = readCache();
  cache[cacheKeyFor(restaurantId, role)] = normalizeRoleUiPermissions(permissions, role);
  localStorage.setItem(ROLE_UI_PERMISSIONS_STORAGE_KEY, JSON.stringify(cache));
  window.dispatchEvent(new Event(ROLE_UI_PERMISSIONS_EVENT));
};
export const fetchRoleUiPermissions = async ({ restaurantId, role }) => {
  if (!restaurantId || !role) return null;
  if (isFullOfflineModeEnabled()) {
    const result = await getOfflineRolePermissions(restaurantId, role);
    const roleData = result?.data?.[normalizeStaffRoleKey(role)] || result?.data;
    return roleData ? normalizeRoleUiPermissions(roleData, role) : null;
  }
  const response = await fetch(`${API_BASE}/role/ui-permissions/get`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ restaurant_id: restaurantId, role }) });
  const result = await response.json().catch(() => null);
  const roleData = result?.data?.[normalizeStaffRoleKey(role)] || result?.data;
  return result?.success && roleData ? normalizeRoleUiPermissions(roleData, role) : null;
};
export const hydrateRoleUiPermissions = async (user) => {
  const role = normalizeStaffRoleKey(user?.role || user?.staffrole || user?.staff_role || user?.user_role);
  const restaurantId = user?.restaurantid || user?.restaurant_id || user?.owner_id || user?.ownerid || user?.parent_id || user?.id;
  if (!CONFIGURABLE_ROLES.includes(role) || !restaurantId) return null;
  const permissions = await fetchRoleUiPermissions({ restaurantId, role });
  if (permissions) cacheRoleUiPermissions({ restaurantId, role, permissions });
  return permissions;
};

const ROUTE_RULES = [
  { prefix: "/reports/restaurants-overview", report: "restaurantsOverview" },
  { prefix: "/reports/table-wise-product-sales", report: "tableWiseProductSales", more: ["TableWiseProductSalesReport"] },
  { prefix: "/reports/table-wise-sales-list", report: "tableWiseSalesList" },
  { prefix: "/reports/inventory/item-performance", report: "itemPerformanceReport", more: ["ItemPerformanceReport"] },
  { prefix: "/reports/inventory/stock-summary", report: "stockSummaryReport", more: ["StockSummaryReport"] },
  { prefix: "/reports/inventory/daily-usage", report: "dailyUsageReport", more: ["DailyUsageReport"] },
  { prefix: "/reports/inventory/low-stock", report: "lowStockReport", more: ["LowStockReport"] },
  { prefix: "/reports/category-performance", report: "categoryPerformance", more: ["CategoryPerformanceReport"] },
  { prefix: "/reports/table-utilization", report: "tableUtilization", more: ["TableUtilizationReport"] },
  { prefix: "/reports/day-wise-category", report: "dayWiseCategoryReport", more: ["DayWiseCategoryReport"] },
  { prefix: "/reports/day-wise-items", report: "dayWiseItemReport", more: ["DayWiseItemReport"] },
  { prefix: "/reports/day-wise-kot", report: "dayWiseKotReport", more: ["DayWiseKOTReport"] },
  { prefix: "/reports/month-wise-items", report: "monthWiseItemReport", more: ["MonthWiseItemReport"] },
  { prefix: "/reports/payment-method", report: "paymentMethodReport", more: ["PaymentMethodReport"] },
  { prefix: "/reports/transaction-summary", report: "transactionSummaryReport", more: ["TransactionSummaryReport"] },
  { prefix: "/reports/split-payment", report: "splitPaymentReport", more: ["SplitPaymentReport"] },
  { prefix: "/reports/credit-payment", report: "creditPaymentReport", more: ["CreditPaymentReport"] },
  { prefix: "/reports/custom-excel", report: "customExcelReport" },
  { prefix: "/reports/discounted-orders", report: "discountedOrdersReport" },
  { prefix: "/reports/tax-report", report: "taxReport" },
  { prefix: "/reports/top-products", report: "topProducts", more: ["TopProductsReport"] },
  { prefix: "/reports/top-waiters", report: "topWaiters", more: ["TopWaitersReport"] },
  { prefix: "/reports/hourly-sales", report: "hourlySales", more: ["HourlySalesReport"] },
  { prefix: "/reports/hourly", report: "hourlySales", more: ["HourlySalesReport"] },
  { prefix: "/finance/profit-loss", report: "profitReport", more: ["ProfitReport"] },
  { prefix: "/finance/day-book", report: "dayBookEntryReport", more: ["DayBookEntryReport"] },
  { prefix: "/finance/report", report: "financeReport", more: ["FinanceReport"] },
  { prefix: "/monthly-summary", report: "monthlySummary", more: ["MonthlySummary"] },
  { prefix: "/sales-report", report: "salesReport", more: ["SalesReports"] },
  { prefix: "/sales-reports", report: "salesReport", more: ["SalesReports"] },
  { prefix: "/sales", report: "salesList", drawer: ["salesList"], more: ["SalesListScreen"] },
  { prefix: "/party-bills", report: "partyBills", more: ["PartyBillsScreen"] },
  { prefix: "/billing/history", more: ["BillingHistory"] },
  { prefix: "/order-edit-history", report: "orderEditHistory" },
  { prefix: "/deleted-orders", report: "deletedOrders", more: ["DeletedOrders"] },
  { prefix: "/dashboard", drawer: ["dashboard"], tabs: ["Dashboard"], alwaysOn: true },
  { prefix: "/notifications", drawer: ["notifications"] },
  { prefix: "/more", drawer: ["allShortcuts"], tabs: ["More"] },
  { prefix: "/tables", tabs: ["TableScreen"], more: ["TableScreen"] },
  { prefix: "/order/", drawer: ["newOrder", "quickBill"], tabs: ["QuickBill"] },
  { prefix: "/kot/waiter", drawer: ["myKOTs"] },
  { prefix: "/kot", drawer: ["kotManagement"] },
  { prefix: "/chef/dashboard", drawer: ["kotManagement"] },
  { prefix: "/chef/profile", drawer: ["profile"], tabs: ["Profile"], more: ["Profile"] },
  { prefix: "/waiter/profile", tabs: ["WaiterProfile"] },
  { prefix: "/profile", drawer: ["profile"], tabs: ["Profile"], more: ["Profile"] },
  { prefix: "/add-category", drawer: ["category"], more: ["AddCategory"] },
  { prefix: "/add-product", drawer: ["addProduct", "productList"], tabs: ["Product List"], more: ["AddProduct", "ProductList"] },
  { prefix: "/digital-menu", drawer: ["digitalMenu"] },
  { prefix: "/bulk-gst", drawer: ["bulkTax"] },
  { prefix: "/price-lists", drawer: ["priceLists"] },
  { prefix: "/add-party", drawer: ["partyManagement"], more: ["AddParty"], customerPage: true },
  { prefix: "/add-table", drawer: ["tableManagement"], more: ["AddTable"] },
  { prefix: "/menu-scanner", drawer: ["scanMenuWithAI"], more: ["MenuScanner", "MenuScannerV2"] },
  { prefix: "/qr/management", drawer: ["qrCodeManagement"], more: ["QRCodeManagementScreen"] },
  { prefix: "/qr/table-order", drawer: ["qrTableOrder"], more: ["QrTableOrder"] },
  { prefix: "/inventory/stock-in", drawer: ["inventoryStockIn"] },
  { prefix: "/inventory/usage", drawer: ["inventoryUsed"] },
  { prefix: "/inventory/manage-items", drawer: ["inventoryManage"] },
  { prefix: "/product-reorder", drawer: ["productReorder"] },
  { prefix: "/staff/manage", drawer: ["staffList"] },
  { prefix: "/finance/tracker", drawer: ["expenseAndIncome"], more: ["FinanceTracker"] },
  { prefix: "/khatabook", drawer: ["khatabook"], more: ["Khatabook"] },
  { prefix: "/role-permissions", drawer: ["rolePermissions"], more: ["RolePermissions"] },
  { prefix: "/calculator", drawer: ["calculator"], more: ["Calculator"] },
  { prefix: "/regional-tax-settings", drawer: ["regionalTax"] },
  { prefix: "/multiple-printers", drawer: ["multiplePrinters"] },
  { prefix: "/menu-customize", drawer: ["customizeDrawer"] },
  { prefix: "/connect-bluetooth", drawer: ["printerConnect"], more: ["ConnectBlueTooth"] },
  { prefix: "/printer-settings", drawer: ["printerSettings"], more: ["POSSettings"] },
  { prefix: "/pos-settings", more: ["POSSettings"] },
  { prefix: "/keyboard-shortcuts", drawer: ["keyboardShortcuts"] },
  { prefix: "/help", drawer: ["helpCenter"], more: ["HelpCenterScreen"] },
  { prefix: "/login-help", drawer: ["loginHelp"] },
  { prefix: "/support", drawer: ["supportChat"], more: ["SupportChatScreen"] },
  { prefix: "/feedback-hub", drawer: ["feedbackHub"], more: ["FeedbackHub"] },
  { prefix: "/feedback", drawer: ["feedbackHub"], more: ["FeedbackHub"] },
  { prefix: "/policies", drawer: ["policies"] },
  { prefix: "/plan-selection", drawer: ["planInfo"] },
  { prefix: "/smart-insights", drawer: ["smartInsights"] },
  { prefix: "/find-us", drawer: ["findUsOn"] },
].sort((a, b) => b.prefix.length - a.prefix.length);

const getRouteRule = (path) => {
  const normalized = String(path || "").trim().toLowerCase();
  return ROUTE_RULES.find(({ prefix }) => normalized.startsWith(prefix)) || null;
};
const roleForUser = (user) => normalizeStaffRoleKey(user?.role || user?.staffrole || user?.staff_role || user?.user_role);
const effectivePermissions = (user) => {
  const role = roleForUser(user);
  return normalizeRoleUiPermissions(getCachedRoleUiPermissions(user) || defaultRoleUiPermissions(role), role);
};
const anyEnabled = (permissions, surface, keys = []) => keys.some((key) => permissions.menus?.[surface]?.[key] !== false);

export const getCustomerManagementPermissions = (user) => {
  const role = roleForUser(user);
  if (!CONFIGURABLE_ROLES.includes(role)) return { page: true, create: true, edit: true, delete: true };
  return effectivePermissions(user).menus.customerManagement;
};

export const canAccessRoleUiRoute = (path, user) => {
  const role = roleForUser(user);
  if (!CONFIGURABLE_ROLES.includes(role)) return true;
  // Keep the established web baseline until the server response is hydrated.
  // Once cached, the configured role permissions become authoritative.
  if (!getCachedRoleUiPermissions(user) && role !== "chef") return true;
  const permissions = effectivePermissions(user);
  const normalized = String(path || "").toLowerCase();
  if (normalized === "/reports" && permissions.reports.page === false) return false;
  const rule = getRouteRule(normalized);
  if (!rule) return role !== "chef";
  if (rule.alwaysOn && role !== "chef") return true;
  if (rule.customerPage) return permissions.menus.customerManagement.page !== false;
  // An individual report is independent from the Reports tab/page. This matches
  // mobile, where a report may also be opened from the More menu.
  if (rule.report) return permissions.reports.items[rule.report] !== false;
  const surfaces = ["drawer", "tabs", "more"];
  const configured = surfaces.filter((surface) => rule[surface]?.length);
  return configured.length === 0 || configured.some((surface) => anyEnabled(permissions, surface, rule[surface]));
};

export const canShowRoleUiNavigation = (path, user, surface, permissionKey = "") => {
  const role = roleForUser(user);
  if (!CONFIGURABLE_ROLES.includes(role)) return true;
  if (!getCachedRoleUiPermissions(user) && role !== "chef") return true;
  const permissions = effectivePermissions(user);
  const normalized = String(path || "").toLowerCase();
  if (normalized === "/reports") {
    return permissions.reports.page !== false && (surface !== "tabs" || permissions.menus.tabs.Reports !== false);
  }
  const rule = getRouteRule(normalized);
  if (!rule) return role !== "chef";
  if (rule.alwaysOn && role !== "chef") return canAccessRoleUiRoute(path, user);
  if (rule.customerPage && permissions.menus.customerManagement.page === false) return false;
  if (rule.report && permissions.reports.items[rule.report] === false) return false;
  if (permissionKey) return permissions.menus?.[surface]?.[permissionKey] !== false;
  if (!rule[surface]?.length) return canAccessRoleUiRoute(path, user);
  return anyEnabled(permissions, surface, rule[surface]);
};

export const reportPermissionKeyForRoute = (path) => getRouteRule(path)?.report || null;
